import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Section from '../components/Section';
import { useWeb3Auth } from '../web3/Web3AuthContext';
import AthleteForm from '../components/AthleteForm';
import SportsAgentForm from '../components/SportsAgentForm';
import SportsOfficerForm from '../components/SportsOfficerForm';
import SportsRepresentativeForm from '../components/SportsRepresentativeForm';
import Button from '../components/Button';
import GetDidCta from '../components/GetDidCta';
import PageHero from '../components/PageHero';
import PathSteps from '../components/PathSteps';
import { SF_UI } from '../components/ui/tokens';
import {
  canUseApplicationForm,
  needsDidOnboarding,
} from '../web3/didEligibility';
import { KYC_ONBOARDING_STEPS } from '../data/classicsPath';


type FormType = 'athlete' | 'agent' | 'officer' | 'representative';

const AthleteFormPage = () => {
  const navigate = useNavigate();
  const web3 = useWeb3Auth();
  const [error] = useState<string | null>(null);
  const [formType, setFormType] = useState<FormType>('athlete');
  const [recheckingDid, setRecheckingDid] = useState(false);
  const isLoading = web3.isConnecting;
  const canApply = web3.isConnected && web3.isCorrectChain;
  const signedIn = web3.isAuthenticated;
  const formUnlocked = canUseApplicationForm(web3.eligibility);
  const checkingDid = canApply && signedIn && !web3.eligibility;

  const renderSignupHeading = () => (
    <div className="text-center mb-8">
      <h2 className="mb-4 text-2xl font-bold sm:text-3xl">Then sign up for SportsFuture</h2>
      <p className="mx-auto max-w-3xl text-base text-gray-600 sm:text-xl">
        Select your role and complete the appropriate application form to join the SportsFuture ecosystem.
      </p>
    </div>
  );

  const renderSignup = () => {
    if (error) {
      return (
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex justify-center items-center min-h-[240px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      );
    }

    if (!canApply) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center"
        >
          {renderSignupHeading()}
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            After wallet, KYC, and APTM for gas, connect on Apertum and sign a message to
            prove you control the wallet. Your wallet and Global DID are recorded only when
            you submit — connecting alone does not create a record.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/auth')}
          >
            Connect Wallet
          </Button>
        </motion.div>
      );
    }

    if (!signedIn) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center"
          data-sf="signup-needs-signature"
        >
          {renderSignupHeading()}
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Sign a message in your wallet to prove you control this account. This does not
            create an application record.
          </p>
          {web3.error && (
            <p className="mb-4 text-sm text-red-600">{web3.error}</p>
          )}
          <Button
            variant="primary"
            size="lg"
            disabled={web3.isSigningIn}
            onClick={() => {
              void web3.signIn();
            }}
          >
            {web3.isSigningIn ? 'Waiting for signature…' : 'Sign to continue'}
          </Button>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-5xl mx-auto"
      >
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8 text-center">
          <p className="text-lg text-primary font-medium">
            During the initial soft launch phase, all applications will be reviewed manually. More Information will follow after Q3.
          </p>
        </div>

        {renderSignupHeading()}

        {checkingDid ? (
          <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            <p className="text-gray-600">Checking this wallet for a Global DID…</p>
          </div>
        ) : !formUnlocked ? (
          <div
            data-sf="signup-blocked"
            className="relative"
            aria-disabled="true"
          >
            <div className="mb-8 flex justify-center">
              {needsDidOnboarding(web3.eligibility?.reason) ? (
                <GetDidCta
                  centered
                  reason={web3.eligibility?.reason}
                  message={web3.eligibility?.message}
                />
              ) : (
                <div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-5 text-center text-amber-950">
                  <p>
                    {web3.eligibility?.message ||
                      'This wallet cannot apply until Global DID KYC is complete.'}
                  </p>
                </div>
              )}
            </div>

            <div className="relative min-h-[280px] rounded-lg border border-gray-200 bg-white">
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-lg bg-slate-50/85 px-6 py-8 text-center backdrop-blur-[1px]">
                <Lock className="text-primary" size={28} />
                <p className="max-w-md font-medium text-gray-800">
                  Application is locked until this connected wallet has a valid Global DID.
                </p>
                <p className="max-w-md text-sm text-gray-600">
                  Finish KYC on Apertum, then check again. Role selection and forms stay
                  unavailable until that is complete.
                </p>
                <Button
                  variant="outline"
                  disabled={recheckingDid}
                  onClick={async () => {
                    setRecheckingDid(true);
                    try {
                      await web3.refreshEligibility();
                    } finally {
                      setRecheckingDid(false);
                    }
                  }}
                >
                  {recheckingDid ? 'Checking…' : 'Check DID again'}
                </Button>
              </div>
              <div
                className="pointer-events-none select-none p-8 opacity-40"
                aria-hidden="true"
              >
                <div className="mb-8 flex flex-wrap justify-center gap-4">
                  <span className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white">
                    Athlete Application
                  </span>
                  <span className="rounded-xl border border-primary/30 bg-white px-5 py-2.5 text-sm font-semibold text-primary">
                    Sports Agent Application
                  </span>
                  <span className="rounded-xl border border-primary/30 bg-white px-5 py-2.5 text-sm font-semibold text-primary">
                    Sports Representative Application
                  </span>
                  <span className="rounded-xl border border-primary/30 bg-white px-5 py-2.5 text-sm font-semibold text-primary">
                    Sports Officer Application
                  </span>
                </div>
                <div className="h-40 rounded-md border border-dashed border-gray-300 bg-gray-50" />
              </div>
            </div>
          </div>
        ) : (
          <div data-sf="signup-form">
            <div className="mb-8">
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  variant={formType === 'athlete' ? 'primary' : 'outline'}
                  onClick={() => setFormType('athlete')}
                >
                  Athlete Application
                </Button>
                <Button
                  variant={formType === 'agent' ? 'primary' : 'outline'}
                  onClick={() => setFormType('agent')}
                >
                  Sports Agent Application
                </Button>
                <Button
                  variant={formType === 'representative' ? 'primary' : 'outline'}
                  onClick={() => setFormType('representative')}
                >
                  Sports Representative Application
                </Button>
                <Button
                  variant={formType === 'officer' ? 'primary' : 'outline'}
                  onClick={() => setFormType('officer')}
                >
                  Sports Officer Application
                </Button>
              </div>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-lg sm:p-8">
              {formType === 'athlete' && <AthleteForm />}
              {formType === 'agent' && <SportsAgentForm />}
              {formType === 'officer' && <SportsOfficerForm />}
              {formType === 'representative' && <SportsRepresentativeForm />}
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <>
      <PageHero
        eyebrow="Apply"
        title="Apply to SportsFuture"
        subtitle="Get KYC'd first: create a wallet, complete identity verification, and get APTM for gas — then sign up below."
      />

      <Section
        bgColor="bg-primary-50"
        title="Get KYC'd first"
        subtitle="Complete these three steps before you sign up for SportsFuture."
        centered
      >
        <div data-sf="kyc-onboarding">
          <PathSteps steps={KYC_ONBOARDING_STEPS} appear="mount" />
        </div>
      </Section>

      <Section bgColor={SF_UI.sectionBand} className="pt-4">
        {renderSignup()}
      </Section>
    </>
  );
};

export default AthleteFormPage;
